import { requisitar } from "./http";

/**
 * O RECIPIENTE — o tambor, a bombona, o IBC.
 *
 * ⚠️ **Não há empresa em nenhum tipo deste arquivo, e não é esquecimento.**
 *
 * O recipiente é a primeira entidade regulada do sistema sem CNPJ (ADR-0012
 * item 2): o mesmo tambor recebe material das três empresas ao longo da vida, e
 * é exatamente isso que o Art. 192 da RDC 654/2022 pergunta — *"o que já esteve
 * nesta bombona antes?"*. Um histórico recortado por empresa responderia "nada
 * esteve aqui antes" para o tambor que teve outro material na semana passada.
 *
 * A atribuição de responsabilidade não se perde: ela mora na **ocupação** (o
 * volume do lote), que tem empresa obrigatória. Por isso as duas chamadas que
 * tocam material — o vínculo e a contagem do mutirão — **exigem** empresa, e as
 * do ativo não.
 */

/** Os quatro estados GRAVADOS. `OCUPADO` não está aqui: ele é derivado. */
export type EstadoDoRecipiente =
  | "EM_QUALIFICACAO"
  | "ATIVO"
  | "BLOQUEADO"
  | "APOSENTADO";

/** Lista fechada, como `TipoDeMaterial`. Valor novo é acréscimo. */
export type TipoDeRecipiente = "BOMBONA" | "TAMBOR" | "IBC";

export interface Recipiente {
  id: string;
  /** `R-0047` — sequencial, gerado, legível e ditável por telefone. */
  codigo: string;

  /* ---- dossiê: nulo até alguém preenchê-lo, e isso é legítimo ---- */

  tipo: TipoDeRecipiente | null;
  /** O tipo por extenso, resolvido no servidor. */
  palavraDoTipo: string | null;
  /** De que ele é feito — texto livre até a Qualidade fechar o vocabulário. */
  construcao: string | null;

  /**
   * ⚠️ **TRÊS GRANDEZAS, e a unidade vai no nome do campo.**
   *
   * Capacidade nominal (L) e massa máxima de trabalho (kg) são do
   * **recipiente**; a quantidade acondicionada (kg **ou** L) é da **ocupação**,
   * e por isso não está aqui. Um tambor de 50 L com massa máxima de 48 kg
   * contendo 47,185 kg de óleo são três números diferentes sobre o mesmo
   * objeto — fundi-los produziria uma comparação que às vezes está certa e
   * ninguém sabe quando.
   *
   * Texto, como toda quantidade: número em JSON vira `double`.
   */
  capacidadeNominalEmLitros: string | null;
  massaMaximaDeTrabalhoEmKg: string | null;
  taraEmKg: string | null;
  comissionadoEm: string | null;

  /* ---- estado: ato de pessoa, com autor, data e motivo ---- */

  estado: EstadoDoRecipiente;
  /** O estado por extenso, resolvido no servidor. */
  palavraDoEstado: string;
  /** O porquê da transição — Art. 265. Nulo só no estado de nascimento. */
  motivoDoEstado: string | null;
  estadoEm: string;

  /** Falta algum campo do dossiê. Enquanto for verdade, ele não se qualifica. */
  dossieCompleto: boolean;

  /**
   * 🔴 **Derivado, nunca gravado**: existe ocupação ativa apontando para ele.
   *
   * Gravá-lo seria a segunda verdade clássica — um `update` esquecido, e o
   * sistema passa a dizer que o tambor está livre com material dentro.
   */
  ocupado: boolean;
}

/**
 * Uma linha do histórico — **e só o que o próximo ocupante precisa saber**.
 *
 * Repare no que não está aqui: código de lote, fornecedor, nota fiscal,
 * quantidade, token. Não é economia de campo — é a regra 3 do ADR-0012 item 2:
 * o histórico atravessa CNPJs, o dado comercial não. Ele é uma **lista**, não um
 * atalho: a ocupação de outra empresa aparece sem link para abrir.
 */
export interface OcupacaoNoHistorico {
  material: string;
  /** O início da ocupação. O fim é a #31 — hoje toda ocupação está ativa. */
  desde: string | null;
}

export interface RecipienteNaTela {
  recipiente: Recipiente;
  historico: OcupacaoNoHistorico[];
}

/** O tamanho do mutirão de campo que falta. */
export interface OcupacoesSemRecipiente {
  semRecipiente: number;
  total: number;
}

export interface DossieDoRecipiente {
  tipo: TipoDeRecipiente | null;
  construcao: string | null;
  capacidadeNominalEmLitros: string | null;
  massaMaximaDeTrabalhoEmKg: string | null;
  taraEmKg: string | null;
  comissionadoEm: string | null;
}

export const recipientes = {
  /**
   * O parque.
   *
   * Os aposentados ficam fora por padrão e **continuam existindo** — some da
   * tela é diferente de some do banco.
   */
  listar: (incluirAposentados = false) =>
    requisitar<Recipiente[]>(
      `/producao/recipientes?incluirAposentados=${incluirAposentados}`,
    ),

  ficha: (id: string) => requisitar<RecipienteNaTela>(`/producao/recipientes/${id}`),

  /** Pelo código da plaqueta — é o que está escrito no tambor. */
  fichaPorCodigo: (codigo: string) =>
    requisitar<RecipienteNaTela>(
      `/producao/recipientes/por-codigo/${encodeURIComponent(codigo)}`,
    ),

  /**
   * Cria a **identidade** de um tambor — e nada mais.
   *
   * Sem corpo: não há código a informar (é gerado), não há estado a escolher
   * (nasce em qualificação) e não há dossiê a digitar. O que sai daqui é um
   * número para escrever na plaqueta.
   */
  identificar: () =>
    requisitar<RecipienteNaTela>("/producao/recipientes", { metodo: "POST" }),

  descreverDossie: (id: string, dossie: DossieDoRecipiente) =>
    requisitar<RecipienteNaTela>(`/producao/recipientes/${id}/dossie`, {
      metodo: "PUT",
      corpo: dossie,
    }),

  /** Motivo obrigatório em **toda** transição — Art. 265. */
  qualificar: (id: string, motivo: string) =>
    requisitar<RecipienteNaTela>(`/producao/recipientes/${id}/qualificacao`, {
      metodo: "POST",
      corpo: { motivo },
    }),

  bloquear: (id: string, motivo: string) =>
    requisitar<RecipienteNaTela>(`/producao/recipientes/${id}/bloqueio`, {
      metodo: "POST",
      corpo: { motivo },
    }),

  /**
   * Aposenta — **inativação lógica**, e não exclusão.
   *
   * Não existe caminho para reativar, e a ausência é o controle. O tambor
   * continua na consulta com `incluirAposentados`, continua respondendo pelo
   * histórico e nunca devolve o código dele para outro.
   */
  aposentar: (id: string, motivo: string) =>
    requisitar<RecipienteNaTela>(`/producao/recipientes/${id}/aposentadoria`, {
      metodo: "POST",
      corpo: { motivo },
    }),

  /**
   * Diz em qual tambor está a ocupação `numero` deste lote.
   *
   * **Com empresa**, e é a regra 2 do ADR-0012 item 2 em exercício: o caminho do
   * recipiente até o material passa pela ocupação, que tem CNPJ. Sem o recorte,
   * quem soubesse o identificador de um lote alheio amarraria tambores ao
   * material de outra empresa.
   */
  vincular: (
    empresa: string,
    loteId: string,
    numeroDoVolume: number,
    codigo: string,
    motivo: string,
  ) =>
    requisitar<{ numeroDoVolume: number; recipiente: string }>(
      `/producao/lotes/${loteId}/volumes/${numeroDoVolume}/recipiente?empresa=${empresa}`,
      { metodo: "POST", corpo: { codigo, motivo } },
    ),

  /**
   * Quantas ocupações ainda estão **sem recipiente identificado**.
   *
   * Nulo é o estado honesto — e é **contável**. Enquanto este número não for
   * zero, o sistema conhece um parque menor que o real, e a diferença é
   * invisível em qualquer outra tela.
   */
  ocupacoesSemRecipiente: (empresas: readonly string[]) =>
    empresas.length === 0
      ? Promise.resolve<OcupacoesSemRecipiente>({ semRecipiente: 0, total: 0 })
      : requisitar<OcupacoesSemRecipiente>(
          `/producao/ocupacoes/sem-recipiente?${empresas
            .map((e) => `empresa=${e}`)
            .join("&")}`,
        ),
};
